import { alpha, createTheme } from '@mui/material/styles'

const PURPLE = '#4F2682'
const LIGHT_PURPLE = '#957db4'
const RED = '#D40202'
const BLUE = '#5BC0DE'
const DARK_BLUE = '#31B0D5'
const GREEN = '#5cb85c'
const DARK_GREEN = '#449D44'
const BROWN = '#68504C'
const DARK_BROWN = '#361611'
const LIGHT_BROWN = '#D6D0C7'
const EXTRA_LIGHT_BROWN = '#F2F0ED'
const WHITE = '#FFFFFF'
const OFF_WHITE = '#FAFAFA'
const BLACK = '#0D0D0D'
const GREY_500 = '#333333'
const GREY_400 = '#969696'
const GREY_300 = '#CED4DA'
const GREY_200 = '#F5F5F5'
const GREY_100 = '#FCFCFC'

let baseTheme = createTheme({
  typography: {
    fontFamily: ['arial', 'verdana', 'tahoma', 'helvetica', 'sans-serif'].join(
      ','
    ),
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 450,
      md: 739,
      lg: 992,
      xl: 1536,
    },
  },
  zIndex: {
    dropdown: 1000,
    sticky: 1100,
    fixed: 1200,
  },
  palette: {
    primary: {
      main: DARK_BROWN,
    },
    secondary: {
      dark: DARK_BROWN,
      light: LIGHT_BROWN,
      extra_light: EXTRA_LIGHT_BROWN,
      main: BROWN,
    },
    purple: {
      main: PURPLE,
      light: LIGHT_PURPLE,
    },
    grey: {
      100: GREY_100,
      200: GREY_200,
      300: GREY_300,
      400: GREY_400,
      500: GREY_500,
    },
    common: {
      white: WHITE,
      off_white: OFF_WHITE,
      black: BLACK,
    },
    info: {
      main: BLUE,
      dark: DARK_BLUE,
    },
    success: {
      main: GREEN,
      dark: DARK_GREEN,
    },
    danger: {
      main: RED,
    },
  },
})

const theme = createTheme(baseTheme, {
  typography: {
    h2: {
      fontSize: '18px',
      fontWeight: 'normal',
      color: baseTheme.palette.primary.main,
      marginTop: '0',
      marginBottom: '20px',
      lineHeight: '1.1',
    },
    h3: {
      fontSize: '16px',
      fontWeight: 'normal',
      color: baseTheme.palette.common.black,
      marginTop: '0',
      marginBottom: '10px',
      lineHeight: '1.1',
    },
    h4: {
      fontSize: '14px',
      fontWeight: 'normal',
      color: baseTheme.palette.common.black,
      marginTop: '0',
      marginBottom: '10px',
      lineHeight: '1.1',
    },
    body1: {
      fontSize: '14px',
      fontWeight: 'normal',
      color: baseTheme.palette.common.black,
    },
    body2: {
      fontSize: '12px',
      fontWeight: 'normal',
      color: baseTheme.palette.common.black,
    },
    button: {
      color: baseTheme.palette.common.black,
      textTransform: 'none',
    },
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: baseTheme.palette.common.black,
          '&.Mui-focused': { color: baseTheme.palette.common.black },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          backgroundColor: baseTheme.palette.info.main,
          color: baseTheme.palette.common.white,
          '&:hover': { backgroundColor: baseTheme.palette.info.dark },
          padding: '6px 24px',
        },
      },
    },
    // MuiTouchRipple: {
    //   styleOverrides: {
    //     child: {
    //       backgroundColor: baseTheme.palette.secondary.light,
    //     },
    //   },
    // },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: baseTheme.palette.secondary.light,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: baseTheme.palette.common.black,
          '&.Mui-selected': { color: baseTheme.palette.common.black },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '16px',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          backgroundColor: baseTheme.palette.grey[100],
          border: '1px solid #ced4da',
          transition: baseTheme.transitions.create([
            'border-color',
            'background-color',
            'box-shadow',
          ]),
          //Last two should only apply to inputs with a label: label + &
          fontSize: '16px',
          marginTop: '18px',
          '&:focus-within': {
            boxShadow: `${alpha(
              baseTheme.palette.primary.main,
              0.25
            )} 0 0 0 1px`,
            borderColor: baseTheme.palette.primary.main,
          },
        },
        input: {
          fontSize: '14px',
          padding: '10px 12px',
          border: 'none',
          position: 'relative',
        },
        adornedEnd: {
          '& .MuiInputAdornment-root': {
            marginLeft: 0,
          },
        },
        inputAdornedEnd: {
          paddingRight: 0,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          width: '100%',
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          // Position Snackbar in bottom left
          [baseTheme.breakpoints.up('xs')]: {
            left: '8px',
            bottom: '8px',
          },

          [baseTheme.breakpoints.down('md')]: {
            left: '0px',
            bottom: '0px',
            width: '100%',
            padding: '8px',

            '& .MuiAlert-root': {
              padding: '0px 16px',
              flexGrow: 1,
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        message: {
          marginTop: '2px',
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: baseTheme.palette.grey[400],
          '&.Mui-active': {
            color: baseTheme.palette.purple.main,
          },
          '&.Mui-completed': {
            color: baseTheme.palette.purple.main,
          },
          '&.Mui-error': {
            color: baseTheme.palette.danger.main,
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          flexGrow: 1,
          backgroundColor: baseTheme.palette.common.off_white,
          transition: baseTheme.transitions.create([
            'color',
            'background-color',
          ]),

          '&.Mui-selected': {
            color: baseTheme.palette.common.off_white,
            backgroundColor: baseTheme.palette.secondary.main,
            '&:hover': {
              backgroundColor: baseTheme.palette.secondary.main,
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    // variants: [
    //   {
    // props: { variant: 'brown' },
    //   style: {
    //     flexGrow: 1,
    //     backgroundColor: baseTheme.palette.common.off_white,
    //     transition: 'background-color linear 250ms',

    //     '&.Mui-selected': {
    //       color: baseTheme.palette.common.off_white,
    //       backgroundColor: baseTheme.palette.secondary.main,
    //       '&:hover': {
    //         backgroundColor: baseTheme.palette.secondary.main,
    //       },
    //     },
    //   },
    // },
    // ],
  },
  // MuiCollapse: {
  //   styleOverrides: {
  //     hidden: {
  //       display: 'none',
  //     },
  //   },
  // },
  // },
})

if (process.env.NODE_ENV !== 'production') console.log('theme', theme)

export default theme
